import type { StatusAcao } from '@/lib/api/acoes';

/**
 * Os dois status da LEITURA (proto:1374, 1381-1382) derivados dos cinco que o
 * banco guarda.
 *
 * ── O que isto NÃO é ───────────────────────────────────────────────────────
 * Não é uma migração. `gab.acao.status` continua com os mesmos cinco valores,
 * o formulário continua escrevendo os cinco, a API continua recebendo os cinco.
 * O protótipo colapsou tudo em Realizada/Pendente; colapsar o BANCO junto
 * apagaria para sempre a diferença entre uma ação que nem começou, uma que está
 * em tramitação e uma que está em obra — informação que alguém digitou e que
 * nenhuma tela do protótipo precisava, mas o mandato precisa. Derivar na
 * leitura custa esta função e é reversível; migrar não é.
 *
 * Por isso a derivação mora AQUI, num lugar só. Espalhada em condicionais de
 * renderização (`status === 'concluida' ? ... : ...`), ela divergiria entre o
 * chip, o KPI e o filtro na primeira vez que alguém mexesse em um dos três — e
 * a tela passaria a contar uma coisa e mostrar outra.
 *
 * ── Por que TRÊS e não dois ────────────────────────────────────────────────
 * `cancelada` não é "pendente". Uma ação cancelada não está pendente de nada:
 * jogá-la em Pendente infla o KPI com trabalho morto e faz a sublinha "com
 * marco definido" contar marcos de ações que ninguém vai cumprir. Ocultá-la
 * também não serve — a soma dos chips deixaria de fechar com o total e uma
 * ação que alguém pode precisar auditar ou reabrir sumiria da tela. Fica como
 * categoria própria: visível, contável e com filtro próprio.
 */

/** O vocabulário do banco, na íntegra. Fonte: `gab.acao.status`. */
export const STATUS_BANCO: readonly StatusAcao[] = [
  'planejada',
  'em_tramitacao',
  'em_andamento',
  'concluida',
  'cancelada',
] as const;

/** Rótulos dos cinco valores do banco — usados onde o valor guardado aparece. */
export const ROTULO_BANCO: Record<StatusAcao, string> = {
  planejada: 'Planejada',
  em_tramitacao: 'Em tramitação',
  em_andamento: 'Em andamento',
  concluida: 'Concluída',
  cancelada: 'Cancelada',
};

export type StatusVisivel = 'realizada' | 'pendente' | 'cancelada';

/** As três categorias da leitura, com os rótulos literais do protótipo. */
export const STATUS_VISIVEIS: ReadonlyArray<{ v: StatusVisivel; l: string }> = [
  { v: 'realizada', l: 'Realizada' },
  { v: 'pendente', l: 'Pendente' },
  { v: 'cancelada', l: 'Cancelada' },
] as const;

/**
 * Mapa explícito, valor a valor. Escrito por extenso de propósito: um `default`
 * que mandasse "todo o resto" para `pendente` faria um sexto status futuro do
 * banco entrar calado na conta de Pendentes. Aqui ele vira `null`, e quem
 * chamar precisa decidir o que fazer.
 */
const MAPA: Record<StatusAcao, StatusVisivel> = {
  concluida: 'realizada',
  planejada: 'pendente',
  em_tramitacao: 'pendente',
  em_andamento: 'pendente',
  cancelada: 'cancelada',
};

/** `null` quando o valor não é um dos cinco conhecidos — nunca um palpite. */
export function derivarStatus(s: StatusAcao): StatusVisivel | null {
  return MAPA[s] ?? null;
}

export function rotuloVisivel(v: StatusVisivel): string {
  return STATUS_VISIVEIS.find((x) => x.v === v)?.l ?? v;
}

export function rotuloBanco(s: StatusAcao): string {
  return ROTULO_BANCO[s] ?? s;
}
